import { useMemo, useState } from 'react'
import {
  getClientesComEntregasCarregadasParaRoteirizacao,
  getClientesComEntregasParaRoteirizacao,
} from '../../../../../services/api/https/deliveries/delivery.http'
import {
  getEnviosParaFrota,
  postRoutesDistance,
  setLoadOrder,
} from '../../../../../services/api/https/deliveries/envio-rota.http'
import { errorHandler } from '../../../../../services/util/error.service'
import * as CarregamentoHttp from '../../../../../services/api/https/deliveries/carregamento.http'
import { parseToMysqlFormat } from '../../../../../services/util/date.service'
import { generateRandomHexColorAvoidingSome } from '../../../../../services/util/random-color.service'
import { getZonasEntrega } from '../../../../../services/api/https/registration/zona-entrega.http'
import {
  buildAgendamentosOptions,
  matchAgendamentoSelecionado,
} from '../../../../../services/util/agendamento-entrega.service'
import {
  buildZonasEntregaOptions,
  buildCityIdsFromZonas,
} from '../../../../../services/util/zona-entrega.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  LOAD_CUSTOMERS: 'Carregando clientes',
  LOAD_DISTANCE: 'Carregando distâncias',
  LOAD_CUSTOMERS_AFTER_UNLOAD: 'Carregando clientes apos descarga',
  SAVE_DELIVERIES: 'Salvando entregas',
  GET_ASSOCIATED_DELIVERIES: 'Adquirindo entregas associadas ao veiculo',
}

const INITIAL_CUSTOMER = {
  id: 0,
  nomeFantasia: '',
  latitude: null,
  longitude: null,
  deliveries: [
    {
      id: 0,
      identifier: '',
      qte_sku: 0,
      weight: 0.0,
      client_id: 0,
      load_order: null,
      selected: false,
    },
  ],
}

const INITIAL_STATE = {
  activeIndex: 0,
  customers: [{ ...INITIAL_CUSTOMER }],
  originalCustomers: [],
  allCustomers: [],
  customersWithoutCoordinates: [],
  associatedDeliveries: [],
  deliveriesToAssociate: [],
  selectedVeiculo: null,
  vehicleStats: {
    qteSku: 0,
    weight: 0,
    steps: 0,
    deliveries: 0,
    ocupation: 0.0,
    qtePallets: 0.0,
    distance: 0,
    valor_nf: 0,
  },
  dataPrevista: new Date(),
  showModalAssociatedDeliveries: false,
  frota: {
    deliveries: [],
  },
  loadCounter: 1,
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  periodo: null,
  veiculos_carregados: false,
  vehiclesFilter: [],
  selectedVeiculoFilter: null,
  isSaving: false,
  // Filter state
  zonasEntrega: [],
  zonasEntregaOptions: [],
  selectedZonas: [],
  ufOptions: [],
  selectedUfs: [],
  citiesOptions: [],
  selectedCities: [],
  bairrosOptions: [],
  selectedBairros: [],
  infoComplementarOptions: [],
  selectedInfoComplementar: [],
  empresasOptions: [],
  selectedEmpresas: [],
  reentregaStatus: null,
  entregasAgendadasStatus: null,
  agendamentosOptions: [],
  selectedAgendamentos: [],
  beepNf: '',
}

export default function useMapaCarregamento() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  const localDispatch = useMemo(() => {
    //Helpers
    const filterClientsWithoutCoordinates = customers => {
      const customersWithoutCoordinates = customers.filter(customer => {
        return customer.latitude === null || customer.longitude === null
      })
      return customersWithoutCoordinates.length > 0 ? customersWithoutCoordinates : []
    }

    const filterClientsWithCoordinates = customers => {
      const customersWithCoordinates = customers.filter(customer => {
        return customer.latitude !== null || customer.longitude !== null
      })
      return customersWithCoordinates.length > 0 ? customersWithCoordinates : []
    }

    const updateVehicleStats = (associatedDeliveries, deliveriesToAssociate, vehicle) => {
      if ((associatedDeliveries.length > 0 || deliveriesToAssociate.length) && vehicle) {
        const vehicleStats = { ...INITIAL_STATE.vehicleStats }
        const deliveries = [...deliveriesToAssociate, ...associatedDeliveries]
        deliveries.forEach(delivery => {
          vehicleStats.qteSku += delivery.qte_sku
          vehicleStats.deliveries++
          vehicleStats.weight += delivery.weight
          vehicleStats.valor_nf += delivery.valor_nf || 0
          if (vehicleStats.qtePallets != null && delivery.qte_total_pallets)
            vehicleStats.qtePallets += delivery.qte_total_pallets
          else vehicleStats.qtePallets = null
        })
        vehicleStats.ocupation = (vehicleStats.weight * 100) / vehicle.capacity_kg
        const customersIds = new Set(deliveries.map(delivery => delivery.client_id))
        vehicleStats.steps = customersIds.size
        return vehicleStats
      } else return { ...INITIAL_STATE.vehicleStats }
    }

    const buildFilterOptions = (customersWithCoordinates) => {
      const allDeliveries = []
      customersWithCoordinates.forEach(c => {
        if (c.deliveries) allDeliveries.push(...c.deliveries)
      })
      const ufOptions = [...new Set(allDeliveries.map(e => e.uf).filter(Boolean))].sort().map(sigla => ({
        label: sigla,
        value: sigla,
      }))
      const citiesOptions = [...new Set(allDeliveries.map(e => e.city).filter(Boolean))].sort().map(c => ({
        label: c,
        value: c,
      }))
      const bairrosOptions = [...new Set(allDeliveries.map(e => e.district).filter(Boolean))].sort().map(c => ({
        label: c,
        value: c,
      }))
      const infoComplementarOptions = [...new Set(allDeliveries.map(e => e.info_complementar).filter(Boolean))].sort().map(c => ({
        label: c,
        value: c,
      }))
      const empresasOptions = [...new Set(allDeliveries.map(e => e.company_name).filter(Boolean))].sort().map(e => ({
        label: e,
        value: e,
      }))
      const agendamentosOptions = buildAgendamentosOptions(allDeliveries)

      return {
        ufOptions,
        citiesOptions,
        bairrosOptions,
        infoComplementarOptions,
        empresasOptions,
        agendamentosOptions,
      }
    }

    const applyFiltersToCustomers = (prevState, customersWithCoord) => {
      const cityIdsDasZonas = buildCityIdsFromZonas(prevState.zonasEntrega, prevState.selectedZonas)

      return customersWithCoord.filter(customer => {
        if (!customer.deliveries || !customer.deliveries.length) return false
        const hasMatchingDelivery = customer.deliveries.some(delivery => {
          if (prevState.selectedZonas.length && !cityIdsDasZonas.has(delivery.city_id)) return false
          if (prevState.selectedUfs.length && !prevState.selectedUfs.includes(delivery.uf)) return false
          if (prevState.selectedCities.length && !prevState.selectedCities.includes(delivery.city)) return false
          if (prevState.selectedBairros.length && !prevState.selectedBairros.includes(delivery.district)) return false
          if (prevState.selectedInfoComplementar.length && !prevState.selectedInfoComplementar.includes(delivery.info_complementar)) return false
          if (prevState.selectedEmpresas.length && !prevState.selectedEmpresas.includes(delivery.company_name)) return false
          if (prevState.reentregaStatus === 12 && delivery.status_id !== 12) return false
          if (prevState.reentregaStatus === false && delivery.status_id === 12) return false
          if (prevState.entregasAgendadasStatus === 'sim' && delivery.agendamento_entrega == null) return false
          if (prevState.entregasAgendadasStatus === 'nao' && delivery.agendamento_entrega != null) return false
          if (!matchAgendamentoSelecionado(delivery, prevState.selectedAgendamentos)) return false
          return true
        })
        return hasMatchingDelivery
      })
    }

    return {
      //Actions
      resetState() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        }))
      },

      loadCustomers() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.LOAD_CUSTOMERS,
        }))
      },

      loadZonasEntrega(tp_id) {
        if (!tp_id) return
        getZonasEntrega(tp_id)
          .then(zonasEntrega => {
            setState(prevState => ({
              ...prevState,
              zonasEntrega,
              zonasEntregaOptions: buildZonasEntregaOptions(zonasEntrega),
            }))
          })
          .catch(() =>
            setState(prevState => ({
              ...prevState,
              zonasEntrega: [],
              zonasEntregaOptions: [],
            })),
          )
      },

      loadCustomersWithDeliveries(tp_id, pInicial, pFinal, isUnload = false) {
        getClientesComEntregasParaRoteirizacao(tp_id, pInicial, pFinal)
          .then(customers => {
            setState(prevState => {
              const customersWithDeliveries = customers.filter(
                customers => customers.deliveries.length > 0,
              )

              const customersWithCoord = filterClientsWithCoordinates(customersWithDeliveries)
              const filterOptions = buildFilterOptions(customersWithCoord)
              const filteredCustomers = applyFiltersToCustomers(prevState, customersWithCoord)

              prevState.allCustomers = customersWithCoord
              prevState.customers = filteredCustomers
              prevState.customersWithoutCoordinates =
                filterClientsWithoutCoordinates(customersWithDeliveries)
              prevState.activeIndex = prevState.customersWithoutCoordinates.length > 0 ? 1 : 0
              if (isUnload)
                prevState.vehicleStats = updateVehicleStats(
                  prevState.associatedDeliveries,
                  prevState.deliveriesToAssociate,
                  prevState.selectedVeiculo,
                )

              prevState.loadingMessage = LOAD_MESSAGE.NONE
              Object.assign(prevState, filterOptions)
              return { ...prevState }
            })
          })
          .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
      },

      loadCustomersWithDeliveriesLoaded(tp_id, expected_date, pInicial, pFinal) {
        getClientesComEntregasCarregadasParaRoteirizacao(tp_id, expected_date, pInicial, pFinal)
          .then(customers => {
            setState(prevState => {
              const customersWithDeliveries = customers.filter(
                customers => customers.deliveries.length > 0,
              )
              const customersLoaded = filterClientsWithCoordinates(customersWithDeliveries)

              const { colors, vehicles } = this.assignVehicleColors(customersLoaded)
              const filterOptions = buildFilterOptions(colors)
              const filteredCustomers = applyFiltersToCustomers(prevState, colors)

              prevState.allCustomers = colors
              prevState.customers = filteredCustomers
              prevState.originalCustomers = colors
              prevState.vehiclesFilter = vehicles
              prevState.selectedVeiculoFilter = vehicles
              prevState.customersWithoutCoordinates =
                filterClientsWithoutCoordinates(customersWithDeliveries)
              prevState.activeIndex = prevState.customersWithoutCoordinates.length > 0 ? 1 : 0
              prevState.loadingMessage = LOAD_MESSAGE.NONE
              Object.assign(prevState, filterOptions)

              return { ...prevState }
            })
          })
          .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
      },

      assignVehicleColors(data) {
        const vehicleColors = {}
        const vehicles = []
        const colors = data.map(client => {
          if (client.vehicles) {
            client.vehicles = client.vehicles.map(vehicle => {
              const vehicleId = vehicle.vehicles_id
              vehicles[vehicleId] = vehicle
              if (!vehicleColors[vehicleId]) {
                vehicleColors[vehicleId] = generateRandomHexColorAvoidingSome()
              }
              return {
                ...vehicle,
                color: vehicleColors[vehicleId],
              }
            })
          }

          return client
        })
        return { colors, vehicles: Object.values(vehicles) }
      },

      getDeliveriesAssociatedToFleet(tp_id, vehicle, expected_date) {
        getEnviosParaFrota(tp_id, vehicle.vehicle_badge, parseToMysqlFormat(expected_date))
          .then(fleet => {
            setState(prevState => ({
              ...prevState,
              frota: fleet,
              associatedDeliveries: fleet.deliveries,
              showModalAssociatedDeliveries: true,
              loadingMessage: LOAD_MESSAGE.NONE,
              loadCounter: Math.max(...fleet.deliveries.map(d => d.load_order)) + 1 || 1,
            }))
          })
          .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
      },

      addDeliveriesToAssociate(customers) {
        setState(prevState => {
          if (prevState.selectedVeiculo) {
            prevState.customers = customers
            prevState.deliveriesToAssociate = []
            customers.forEach(customer =>
              prevState.deliveriesToAssociate.push(
                ...customer.deliveries.filter(delivery => delivery.selected),
              ),
            )

            prevState.vehicleStats = updateVehicleStats(
              prevState.associatedDeliveries,
              prevState.deliveriesToAssociate,
              prevState.selectedVeiculo,
            )
          } else {
            prevState.errorMessage = 'Selecione um veiculo para associar as entregas'
          }
          return { ...prevState }
        })
      },

      saveDeliveriesToVehicle(selectedTp, deliveriesToAssociate, expected_date, vehicle_badge) {
        CarregamentoHttp.saveCarregamento(selectedTp, deliveriesToAssociate)
          .then(() => {
            getEnviosParaFrota(selectedTp, vehicle_badge, parseToMysqlFormat(expected_date))
              .then(fleet => {
                setState(prevState => {
                  prevState.associatedDeliveries = [
                    ...prevState.associatedDeliveries,
                    ...prevState.deliveriesToAssociate,
                  ]
                  prevState.deliveriesToAssociate = []
                  const maxLoadOrder = Math.max(
                    ...prevState.associatedDeliveries.map(d => d.load_order),
                    0,
                  )

                  prevState.associatedDeliveries.forEach(d => {
                    fleet.deliveries.forEach(delivery => {
                      if (d.identifier === delivery.identifier) {
                        delivery.load_order = maxLoadOrder - d.load_order + 1
                      }
                    })
                  })
                  prevState.associatedDeliveries = prevState.associatedDeliveries.sort(
                    (a, b) => b.load_order - a.load_order,
                  )

                  prevState.vehicleStats = updateVehicleStats(
                    prevState.associatedDeliveries,
                    prevState.deliveriesToAssociate,
                    prevState.selectedVeiculo,
                  )
                  setState({ ...prevState })

                  setLoadOrder(fleet.deliveries)
                    .then(() => {
                      setState(prevState => ({
                        ...prevState,
                        loadingMessage: LOAD_MESSAGE.LOAD_CUSTOMERS,
                      }))
                    })
                    .catch(err =>
                      setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })),
                    )
                  return { ...prevState }
                })
              })
              .catch(err =>
                setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })),
              )
          })
          .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
      },

      buildAssociation(deliveries, vehicle, dataPrevista) {
        const associations = []

        const delivery_step_ids = [...new Set(deliveries.map(d => d.step_id))]
        if (!delivery_step_ids.length) return []
        associations.push({
          vehicle_id: vehicle.id,
          driver_id: vehicle.default_driver_id,
          expected_date: parseToMysqlFormat(dataPrevista),
          delivery_step_ids: delivery_step_ids,
        })
        return associations
      },

      updateLoadCounter(type) {
        if (type === 'inc')
          setState(prevState => ({ ...prevState, loadCounter: prevState.loadCounter + 1 }))
        else setState(prevState => ({ ...prevState, loadCounter: prevState.loadCounter - 1 }))
      },

      //Events
      onTabChange(value) {
        if (value === 0)
          setState(prevState => ({
            ...prevState,
            activeIndex: value,
            loadingMessage: LOAD_MESSAGE.LOAD_CUSTOMERS,
          }))
        else setState(prevState => ({ ...prevState, activeIndex: value }))
      },

      onSelectedVeiculoChange(value, tp_id, expected_date) {
        getEnviosParaFrota(tp_id, value.vehicle_badge, parseToMysqlFormat(expected_date))
          .then(fleet => {
            setState(prevState => ({
              ...prevState,
              associatedDeliveries: fleet.deliveries,
              loadCounter: Math.max(...fleet.deliveries.map(d => d.load_order)) + 1,
              vehicleStats: updateVehicleStats(
                fleet.deliveries,
                prevState.deliveriesToAssociate,
                value,
              ),
            }))
          })
          .catch(() =>
            setState(prevState => ({
              ...prevState,
              vehicleStats:
                prevState.deliveriesToAssociate.length > 0
                  ? updateVehicleStats([], prevState.deliveriesToAssociate, value, 0)
                  : { ...INITIAL_STATE.vehicleStats },
              associatedDeliveries: [],
              loadCounter: 1,
            })),
          )
        setState(prevState => ({
          ...prevState,
          selectedVeiculo: value,
        }))
      },

      onSelectedVeiculoFilterChange(value) {
        setState(prevState => ({
          ...prevState,
          selectedVeiculoFilter: value,
          customers: value.length
            ? prevState.originalCustomers.filter(
                customer =>
                  (customer.vehicles &&
                    customer.vehicles.some(vehicle =>
                      value.some(
                        selectedVehicle => selectedVehicle.vehicles_id === vehicle.vehicles_id,
                      ),
                    )) ||
                  !customer.vehicles,
              )
            : prevState.originalCustomers,
        }))
      },

      onDataPrevistaChange(value, selectedVeiculo, selectedTp) {
        setState(prevState => ({ ...prevState, dataPrevista: value }))
        this.onSelectedVeiculoChange(selectedVeiculo, selectedTp, value)
      },

      onInit(periodo) {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.LOAD_CUSTOMERS,
          vehicleStats: { ...INITIAL_STATE.vehicleStats },
          periodo: periodo,
        }))
      },

      onSalvarClick() {
        setState(p =>
          p.isSaving ? p : { ...p, isSaving: true, loadingMessage: LOAD_MESSAGE.SAVE_DELIVERIES },
        )
      },

      onShowAssociatedDeliveries(vehicle) {
        if (vehicle)
          setState(prevState => ({
            ...prevState,
            loadingMessage: LOAD_MESSAGE.GET_ASSOCIATED_DELIVERIES,
          }))
        else
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione um veiculo para associar as entregas',
          }))
      },

      onModalClose(fleets) {
        if (fleets) {
          setState(prevState => {
            prevState.showModalAssociatedDeliveries = false
            const vehicle = fleets.find(veh => veh.fleet_id === prevState.frota.fleet_id)

            if (vehicle) {
              prevState.associatedDeliveries = vehicle.deliveries
              prevState.loadCounter = Math.max(...vehicle.deliveries.map(d => d.load_order)) + 1
            } else {
              prevState.loadCounter = 1
              prevState.associatedDeliveries = []
            }
            prevState.vehicleStats = updateVehicleStats(
              prevState.associatedDeliveries,
              prevState.deliveriesToAssociate,
              prevState.selectedVeiculo,
            )
            prevState.frota = {}
            prevState.loadingMessage = LOAD_MESSAGE.LOAD_CUSTOMERS
            return { ...prevState }
          })
        } else
          setState(prevState => ({ ...prevState, showModalAssociatedDeliveries: false, frota: {} }))
      },

      getCustomersWithDeliveries(pInicial, pFinal) {
        setState(prevState => ({
          ...prevState,
          periodo: { dataInicial: pInicial, dataFinal: pFinal },
          loadingMessage: LOAD_MESSAGE.LOAD_CUSTOMERS,
          activeIndex: 0,
        }))
      },

      onChangeVeiculosCarregados(value) {
        setState(prevState => ({
          ...prevState,
          veiculos_carregados: value,
          loadingMessage: LOAD_MESSAGE.LOAD_CUSTOMERS,
        }))
      },

      onClickLoadDistance() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.LOAD_DISTANCE,
        }))
      },

      async getRouteDistance(tp_id, deliveries) {
        const waypoints = deliveries.map(delivery => ({
          latitude: delivery.latitude,
          longitude: delivery.longitude,
        }))

        const { distance } = await postRoutesDistance(tp_id, waypoints)

        setState(prevState => ({
          ...prevState,
          vehicleStats: { ...prevState.vehicleStats, distance: distance ? distance / 1000 : 0 },
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      },

      // Filter methods
      setFilter(key, value) {
        setState(prevState => {
          const newState = { ...prevState, [key]: value }
          newState.customers = applyFiltersToCustomers(newState, prevState.allCustomers)
          return newState
        })
      },

      limparFiltros() {
        setState(prevState => {
          const newState = {
            ...prevState,
            selectedZonas: [],
            selectedUfs: [],
            selectedCities: [],
            selectedBairros: [],
            selectedInfoComplementar: [],
            selectedEmpresas: [],
            reentregaStatus: null,
            entregasAgendadasStatus: null,
            selectedAgendamentos: [],
            beepNf: '',
          }
          newState.customers = applyFiltersToCustomers(newState, prevState.allCustomers)
          return newState
        })
      },
    }
  }, [])

  return { state, localDispatch }
}
